import React, { useState, useEffect } from 'react';
import { FiX, FiCheck, FiLoader } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { resumeApi } from '../../services/api';

const BUILTIN_STYLES = [
  { id: 'classic', name: 'Classic', description: 'Original design with centered name and horizontal dividers' },
  { id: 'modern', name: 'Modern', description: 'Contemporary design with left-aligned name and blue accent colors' },
  { id: 'minimalist', name: 'Minimalist', description: 'Clean, centered design with minimal decorations' },
];

/** Tiny preview strip for each style in the choice modal */
function TinyStylePreview({ styleId }) {
  const base = 'rounded overflow-hidden border border-gray-200 flex-shrink-0';
  if (styleId === 'classic') {
    return (
      <div className={`${base} bg-white p-1.5 h-14 flex flex-col justify-center`}>
        <div className="text-center text-[8px] font-bold text-gray-800 leading-tight">Name</div>
        <div className="border-t border-gray-400 my-0.5 mx-4" />
        <div className="text-center text-[6px] uppercase text-gray-500 tracking-wide">Section</div>
      </div>
    );
  }
  if (styleId === 'modern') {
    return (
      <div className={`${base} h-14 flex flex-col`}>
        <div className="bg-[#877D75] px-1.5 py-0.5 flex items-center justify-between flex-1 min-h-0">
          <span className="text-[7px] font-bold text-white truncate">Name</span>
          <span className="text-[5px] text-white/90">email</span>
        </div>
        <div className="px-1.5 py-0.5 bg-white">
          <span className="text-[6px] uppercase text-gray-500 font-semibold">Experience</span>
        </div>
      </div>
    );
  }
  if (styleId === 'minimalist') {
    return (
      <div className={`${base} bg-white p-1.5 h-14 flex flex-col justify-center`}>
        <div className="text-center text-[8px] font-bold text-gray-900 leading-tight">Name</div>
        <div className="border-b-2 border-gray-800 my-0.5 mx-2" />
        <div className="text-[6px] text-gray-600 uppercase tracking-wide">Section</div>
      </div>
    );
  }
  if (styleId === 'customized') {
    return (
      <div className={`${base} bg-gray-50 h-14 flex items-center justify-center`}>
        <div className="flex gap-0.5">
          {['#877D75', '#8B8B8B', '#333', '#2C2C2C'].map((c) => (
            <div key={c} className="w-2 h-4 rounded-sm" style={{ backgroundColor: c }} title="Your colors" />
          ))}
        </div>
      </div>
    );
  }
  return null;
}

const ResumeStyleModal = ({ show, onClose, resumeId, versionId, suggestedFileName = null, job = null, resumeContent = null }) => {
  const [selectedStyle, setSelectedStyle] = useState('classic');
  const [selectedCustomStyleId, setSelectedCustomStyleId] = useState(null);
  const [fileFormat, setFileFormat] = useState('pdf');
  const [generating, setGenerating] = useState(false);
  const [customStyles, setCustomStyles] = useState([]);
  const [loadingCustomStyles, setLoadingCustomStyles] = useState(false);
  const [showStyleBuilder, setShowStyleBuilder] = useState(false);
  const [editingStyleId, setEditingStyleId] = useState(null);
  const [downloadAsFileName, setDownloadAsFileName] = useState('');

  useEffect(() => {
    if (show) {
      setSelectedStyle('classic');
      setSelectedCustomStyleId(null);
      setShowStyleBuilder(false);
      setEditingStyleId(null);
      setDownloadAsFileName(suggestedFileName || '');
      loadCustomStyles();
    }
  }, [show, suggestedFileName]);

  const loadCustomStyles = async () => {
    setLoadingCustomStyles(true);
    try {
      const res = await resumeApi.listCustomStyles();
      setCustomStyles(res.data || []);
    } catch (err) {
      console.error('Failed to load custom styles', err);
      toast.error('Failed to load your saved styles.');
      setCustomStyles([]);
    } finally {
      setLoadingCustomStyles(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!resumeId) {
      console.error('Missing resume ID');
      return;
    }
    if (selectedStyle === 'customized' && !selectedCustomStyleId) {
      toast.error('Please select one of your saved custom styles or create a new one.');
      return;
    }

    try {
      setGenerating(true);
      const version = versionId || 'latest';
      const resumeStyleParam = selectedStyle === 'customized' ? selectedCustomStyleId : selectedStyle;
      const options = {};
      if (downloadAsFileName && String(downloadAsFileName).trim()) {
        let name = String(downloadAsFileName).trim();
        const ext = fileFormat === 'docx' ? '.docx' : '.pdf';
        if (name.toLowerCase().endsWith('.docx') || name.toLowerCase().endsWith('.pdf')) {
          name = name.replace(/\.[pP][dD][fF]$|\.[dD][oO][cC][xX]$/, ext);
        } else {
          name = name + ext;
        }
        options.download_filename = name;
      }
      const response = await resumeApi.generatePDF(resumeId, version, resumeStyleParam, fileFormat, options);
      const { fileUrl, pageCount } = response.data || {};
      if (!fileUrl) {
        toast.error('Failed to generate PDF. No download URL received.');
        return;
      }
      window.open(fileUrl, '_blank');
      toast.success(`${fileFormat.toUpperCase()} generated successfully. Starting download...`);
      if (pageCount) {
        toast.info(`Resume PDF page count: ${pageCount}`);
      }
      onClose();
    } catch (error) {
      console.error('PDF Generation Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate file');
    } finally {
      setGenerating(false);
    }
  };

  const canDownload = selectedStyle !== 'customized' || selectedCustomStyleId;

  if (!show) return null;

  // When style builder is open, show it as the only modal (larger, not blocked by parent)
  if (showStyleBuilder) {
    return (
      <StyleBuilderModal
        customStyles={customStyles}
        editingStyleId={editingStyleId}
        onClose={() => {
          setShowStyleBuilder(false);
          setEditingStyleId(null);
          loadCustomStyles();
        }}
        onSaved={(newStyle) => {
          setCustomStyles((prev) => (editingStyleId ? prev.map((s) => (s._id === editingStyleId ? newStyle : s)) : [...prev, newStyle]));
          setSelectedCustomStyleId(newStyle._id);
          setShowStyleBuilder(false);
          setEditingStyleId(null);
          loadCustomStyles();
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Choose Resume Style</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500" aria-label="Close style picker">
            <FiX className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-2">Select a visual style for your resume PDF.</p>
        <p className="text-xs text-gray-400 mb-4">
          Using {versionId ? `version: ${(versionId || '').split('_')[0]}` : 'latest version'}
        </p>

        <div className="grid grid-cols-1 gap-4 mb-4">
          {BUILTIN_STYLES.map((style) => (
            <div
              key={style.id}
              className={`relative bg-white border rounded-lg shadow-sm p-4 flex items-center gap-3 cursor-pointer focus:outline-none ${
                selectedStyle === style.id ? 'border-primary-500 ring-2 ring-primary-500' : 'border-gray-300'
              }`}
              onClick={() => setSelectedStyle(style.id)}
            >
              <div className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-gray-900">{style.name}</span>
                <span className="mt-0.5 block text-sm text-gray-500">{style.description}</span>
              </div>
              <div className="w-20 flex-shrink-0">
                <TinyStylePreview styleId={style.id} />
              </div>
              {selectedStyle === style.id && (
                <div className="h-5 w-5 text-primary-600 absolute top-4 right-24">
                  <FiCheck className="h-5 w-5" />
                </div>
              )}
            </div>
          ))}

          <div
            className={`relative bg-white border rounded-lg shadow-sm p-4 flex items-center gap-3 cursor-pointer focus:outline-none ${
              selectedStyle === 'customized' ? 'border-primary-500 ring-2 ring-primary-500' : 'border-gray-300'
            }`}
            onClick={() => setSelectedStyle('customized')}
          >
            <div className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-gray-900">Customized</span>
              <span className="mt-0.5 block text-sm text-gray-500">Use your saved colors and fonts</span>
            </div>
            <div className="w-20 flex-shrink-0">
              <TinyStylePreview styleId="customized" />
            </div>
            {selectedStyle === 'customized' && (
              <div className="h-5 w-5 text-primary-600 absolute top-4 right-24">
                <FiCheck className="h-5 w-5" />
              </div>
            )}
          </div>
        </div>

        {selectedStyle === 'customized' && (
          <div className="mb-6 pl-1">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Your saved styles</label>
              <button
                type="button"
                className="text-sm text-primary-600 hover:text-primary-700"
                onClick={() => {
                  setEditingStyleId(null);
                  setShowStyleBuilder(true);
                }}
              >
                + Create new style
              </button>
            </div>
            {loadingCustomStyles ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : customStyles.length === 0 ? (
              <p className="text-sm text-gray-500">No custom styles yet. Create one to use your own colors and fonts.</p>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {customStyles.map((cs) => (
                  <div
                    key={cs._id}
                    className={`flex items-center justify-between rounded border p-3 ${
                      selectedCustomStyleId === cs._id ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
                    }`}
                  >
                    <button
                      type="button"
                      className="flex-1 text-left text-sm font-medium text-gray-900"
                      onClick={() => setSelectedCustomStyleId(cs._id)}
                    >
                      {cs.name} <span className="text-gray-500 font-normal">(based on {cs.baseStyle})</span>
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingStyleId(cs._id);
                          setShowStyleBuilder(true);
                        }}
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 text-sm"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Delete "${cs.name}"?`)) return;
                          try {
                            await resumeApi.deleteCustomStyle(cs._id);
                            if (selectedCustomStyleId === cs._id) setSelectedCustomStyleId(null);
                            loadCustomStyles();
                            toast.success('Style deleted.');
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to delete style');
                          }
                        }}
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">File Format</label>
          <div className="flex items-center space-x-4">
            <div
              className={`flex items-center cursor-pointer ${fileFormat === 'pdf' ? 'text-primary-600' : 'text-gray-500'}`}
              onClick={() => setFileFormat('pdf')}
            >
              <div
                className={`w-4 h-4 rounded-full border ${
                  fileFormat === 'pdf' ? 'border-primary-600 bg-primary-600' : 'border-gray-300'
                } flex items-center justify-center`}
              >
                {fileFormat === 'pdf' && <div className="w-2 h-2 rounded-full bg-white" />}
              </div>
              <span className="ml-2 text-sm">PDF</span>
            </div>
            <div
              className={`flex items-center cursor-pointer ${fileFormat === 'docx' ? 'text-primary-600' : 'text-gray-500'}`}
              onClick={() => setFileFormat('docx')}
            >
              <div
                className={`w-4 h-4 rounded-full border ${
                  fileFormat === 'docx' ? 'border-primary-600 bg-primary-600' : 'border-gray-300'
                } flex items-center justify-center`}
              >
                {fileFormat === 'docx' && <div className="w-2 h-2 rounded-full bg-white" />}
              </div>
              <span className="ml-2 text-sm">DOCX</span>
            </div>
          </div>
        </div>

        {suggestedFileName != null && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Download as</label>
            <input
              type="text"
              value={downloadAsFileName}
              onChange={(e) => setDownloadAsFileName(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Name - Resume for Job.pdf"
              disabled={generating}
            />
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            className="btn-outline btn-md mr-3"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-md"
            onClick={handleGeneratePDF}
            disabled={generating || !canDownload}
          >
            {generating ? (
              <>
                <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Generating...
              </>
            ) : (
              `Download ${fileFormat.toUpperCase()}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Custom styles only support Classic or Modern base
const BASE_STYLE_PARAMS = {
  classic: { colors: false, labels: {} },
  modern: {
    colors: true,
    labels: { header: 'Header', section: 'Sections', body: 'Body text', accent: 'Accent' },
  },
};

function StylePreview({ headerColor, sectionColor, bodyTextColor, accentColor, fontFamily, baseStyle, lineSpacing, sectionSpacing, sectionLineBreaks }) {
  const hex = (c) => (c && /^#?[0-9A-Fa-f]{6}$/.test(c.replace('#', '')) ? (c.startsWith('#') ? c : `#${c}`) : '#333333');
  const s = hex(sectionColor);
  const b = hex(bodyTextColor);
  const a = hex(accentColor);
  const h = hex(headerColor);
  const font = fontFamily || 'Helvetica, Arial, sans-serif';
  const lead = typeof lineSpacing === 'number' ? lineSpacing : 1;
  const secMult = typeof sectionSpacing === 'number' ? sectionSpacing : 1;
  const baseGap = sectionLineBreaks ? 0.75 : 0.4;
  const sectionGap = `${baseGap * secMult}rem`;

  const Divider = () => (sectionLineBreaks ? <hr className="border-t border-gray-400 my-1.5" /> : null);

  const Section = ({ title, children }) => (
    <div style={{ marginTop: sectionGap }}>
      <div style={{ color: s || '#333', fontFamily: font, fontWeight: 700, fontSize: '10px', letterSpacing: '0.5px', marginBottom: '4px' }} className="uppercase">{title}</div>
      {children}
    </div>
  );

  const Block = ({ title, sub, body }) => (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontFamily: font, fontSize: '10px', color: a || '#111', fontWeight: 600 }}>{title}</div>
      {sub && <div style={{ fontFamily: font, fontSize: '9px', color: b || '#555', marginTop: 1 }}>{sub}</div>}
      {body && <div style={{ fontFamily: font, fontSize: '9px', color: b || '#333', lineHeight: lead, marginTop: 2 }}>{body}</div>}
    </div>
  );

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden shadow-inner bg-white">
      <div className="text-center py-2 px-3 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wide">
        Preview
      </div>
      <div className="p-4 min-h-[320px] flex flex-col text-left">
        {baseStyle === 'classic' && (
          <>
            <div className="text-center mb-2">
              <div style={{ fontFamily: font, fontWeight: 700, fontSize: '15px', color: '#111' }}>Your Name</div>
              <div style={{ fontFamily: font, fontSize: '9px', color: '#333', marginTop: 1 }}>email@example.com · City, State</div>
            </div>
            <Divider />
            <Section title="Summary">
              <div style={{ fontFamily: font, fontSize: '9px', color: b || '#333', lineHeight: lead }}>Brief summary of your experience and goals.</div>
            </Section>
            <Divider />
            <Section title="Experience">
              <Block title="Senior Role · Company A" sub="2020 – Present" body="Key achievements and responsibilities." />
              <Block title="Previous Role · Company B" sub="2018 – 2020" body="Contributions and outcomes." />
            </Section>
            <Divider />
            <Section title="Education">
              <Block title="Degree Name" sub="University Name · Year" />
            </Section>
            <Divider />
            <Section title="Skills">
              <div style={{ fontFamily: font, fontSize: '9px', color: b || '#333', lineHeight: lead }}>Skill one, Skill two, Skill three</div>
            </Section>
          </>
        )}
        {baseStyle === 'modern' && (
          <>
            <div
              style={{ backgroundColor: h, color: '#fff', padding: '10px 12px', margin: '-16px -16px 10px -16px' }}
              className="flex items-center justify-between"
            >
              <span style={{ fontFamily: font, fontWeight: 700, fontSize: '16px' }}>Your Name</span>
              <span style={{ fontFamily: font, fontSize: '9px', opacity: 0.95 }}>email@example.com</span>
            </div>
            <Divider />
            <Section title="Summary">
              <div style={{ fontFamily: font, fontSize: '9px', color: b, lineHeight: lead }}>Brief summary of your experience and goals.</div>
            </Section>
            <Divider />
            <Section title="Experience">
              <Block title="Senior Role · Company A" sub="2020 – Present" body="Key achievements." />
              <Block title="Previous Role · Company B" sub="2018 – 2020" body="Contributions." />
            </Section>
            <Divider />
            <Section title="Education">
              <Block title="Degree" sub="University · Year" />
            </Section>
            <Divider />
            <Section title="Skills">
              <div style={{ fontFamily: font, fontSize: '9px', color: b, lineHeight: lead }}>Skill one, Skill two, Skill three</div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function StyleBuilderModal({ customStyles, editingStyleId, onClose, onSaved }) {
  const baseOptions = [
    { id: 'classic', name: 'Classic', description: 'Centered name, horizontal dividers' },
    { id: 'modern', name: 'Modern', description: 'Left-aligned name, gray header' },
  ];
  const fontOptions = ['Helvetica', 'Garamond', 'Lato', 'Roboto'];
  const LINE_SPACING_MIN = 0.8;
  const LINE_SPACING_MAX = 1.6;
  const SECTION_SPACING_MIN = 0.5;
  const SECTION_SPACING_MAX = 2;

  const editing = customStyles.find((s) => s._id === editingStyleId) || null;

  const [name, setName] = useState(editing?.name || '');
  const [baseStyle, setBaseStyle] = useState(editing?.baseStyle === 'minimalist' ? 'modern' : (editing?.baseStyle || 'modern'));
  const [headerColor, setHeaderColor] = useState(editing?.overrides?.headerColor || '#877D75');
  const [sectionColor, setSectionColor] = useState(editing?.overrides?.sectionColor || '#8B8B8B');
  const [bodyTextColor, setBodyTextColor] = useState(editing?.overrides?.bodyTextColor || '#333333');
  const [accentColor, setAccentColor] = useState(editing?.overrides?.accentColor || '#2C2C2C');
  const [fontFamily, setFontFamily] = useState(editing?.overrides?.fontFamily || '');
  const [lineSpacingAuto, setLineSpacingAuto] = useState(editing?.overrides?.lineSpacing == null);
  const [lineSpacing, setLineSpacing] = useState(editing?.overrides?.lineSpacing ?? 1);
  const [sectionSpacingAuto, setSectionSpacingAuto] = useState(editing?.overrides?.sectionSpacing == null);
  const [sectionSpacing, setSectionSpacing] = useState(editing?.overrides?.sectionSpacing ?? 1);
  const [sectionLineBreaks, setSectionLineBreaks] = useState(editing?.overrides?.sectionLineBreaks ?? false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (editing) {
      setName(editing.name);
      setBaseStyle(editing.baseStyle === 'minimalist' ? 'modern' : editing.baseStyle);
      setHeaderColor(editing.overrides?.headerColor || '#877D75');
      setSectionColor(editing.overrides?.sectionColor || '#8B8B8B');
      setBodyTextColor(editing.overrides?.bodyTextColor || '#333333');
      setAccentColor(editing.overrides?.accentColor || '#2C2C2C');
      setFontFamily(editing.overrides?.fontFamily || '');
      setLineSpacingAuto(editing.overrides?.lineSpacing == null);
      setLineSpacing(editing.overrides?.lineSpacing ?? 1);
      setSectionSpacingAuto(editing.overrides?.sectionSpacing == null);
      setSectionSpacing(editing.overrides?.sectionSpacing ?? 1);
      setSectionLineBreaks(editing.overrides?.sectionLineBreaks ?? false);
    } else {
      setName('');
      setBaseStyle('modern');
      setHeaderColor('#877D75');
      setSectionColor('#8B8B8B');
      setBodyTextColor('#333333');
      setAccentColor('#2C2C2C');
      setFontFamily('');
      setLineSpacingAuto(true);
      setLineSpacing(1);
      setSectionSpacingAuto(true);
      setSectionSpacing(1);
      setSectionLineBreaks(false);
    }
  }, [editing, editingStyleId]);

  const baseParams = BASE_STYLE_PARAMS[baseStyle] || BASE_STYLE_PARAMS.modern;

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Please enter a style name.');
      return;
    }
    const overrides = {};
    if (baseParams.colors) {
      if (headerColor) overrides.headerColor = headerColor.replace(/^#?/, '#');
      if (sectionColor) overrides.sectionColor = sectionColor.replace(/^#?/, '#');
      if (bodyTextColor) overrides.bodyTextColor = bodyTextColor.replace(/^#?/, '#');
      if (accentColor) overrides.accentColor = accentColor.replace(/^#?/, '#');
    }
    if (fontFamily) overrides.fontFamily = fontFamily;
    if (!lineSpacingAuto) overrides.lineSpacing = lineSpacing;
    if (!sectionSpacingAuto) overrides.sectionSpacing = sectionSpacing;
    overrides.sectionLineBreaks = sectionLineBreaks;

    setSaving(true);
    try {
      if (editingStyleId) {
        const res = await resumeApi.updateCustomStyle(editingStyleId, { name: trimmedName, baseStyle: baseStyle, overrides });
        toast.success('Style updated.');
        onSaved(res.data);
      } else {
        const res = await resumeApi.createCustomStyle({ name: trimmedName, baseStyle, overrides });
        toast.success('Style created.');
        onSaved(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save style');
    } finally {
      setSaving(false);
    }
  };

  const toColorValue = (c) => (c && typeof c === 'string' ? c.replace(/^#?/, '#').slice(0, 7) : '#333333');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">{editingStyleId ? 'Edit style' : 'Create custom style'}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <FiX className="h-6 w-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Left: form */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Style name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. My Blue Modern"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Base style</label>
                <div className="grid grid-cols-1 gap-2">
                  {baseOptions.map((opt) => (
                    <div
                      key={opt.id}
                      className={`border rounded-lg p-3 cursor-pointer ${baseStyle === opt.id ? 'border-primary-500 ring-1 ring-primary-500' : 'border-gray-200'}`}
                      onClick={() => setBaseStyle(opt.id)}
                    >
                      <span className="text-sm font-medium text-gray-900">{opt.name}</span>
                      <span className="block text-xs text-gray-500">{opt.description}</span>
                    </div>
                  ))}
                </div>
              </div>
              {baseParams.colors && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Colors (optional)</label>
                  <div className="space-y-3">
                    {[
                      { key: 'header', label: baseParams.labels.header, value: headerColor, set: setHeaderColor },
                      { key: 'section', label: baseParams.labels.section, value: sectionColor, set: setSectionColor },
                      { key: 'body', label: baseParams.labels.body, value: bodyTextColor, set: setBodyTextColor },
                      { key: 'accent', label: baseParams.labels.accent, value: accentColor, set: setAccentColor },
                    ].map(({ key, label, value, set }) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-24">{label}</span>
                        <input
                          type="color"
                          value={toColorValue(value)}
                          onChange={(e) => set(e.target.value)}
                          className="h-8 w-12 rounded border border-gray-300 cursor-pointer flex-shrink-0"
                        />
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => set(e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font (optional)</label>
                <select
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Default</option>
                  {fontOptions.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Line spacing</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={lineSpacingAuto}
                      onChange={(e) => setLineSpacingAuto(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Auto
                  </label>
                  <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                    <input
                      type="range"
                      min={LINE_SPACING_MIN}
                      max={LINE_SPACING_MAX}
                      step={0.1}
                      value={lineSpacing}
                      onChange={(e) => setLineSpacing(Number(e.target.value))}
                      disabled={lineSpacingAuto}
                      className="flex-1 h-2 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed accent-primary-600"
                    />
                    <span className="text-sm text-gray-600 w-10">{lineSpacingAuto ? '—' : lineSpacing.toFixed(1)}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">Auto uses the style default; adjust slider for custom line height.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Space between sections</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={sectionSpacingAuto}
                      onChange={(e) => setSectionSpacingAuto(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Auto
                  </label>
                  <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                    <input
                      type="range"
                      min={SECTION_SPACING_MIN}
                      max={SECTION_SPACING_MAX}
                      step={0.1}
                      value={sectionSpacing}
                      onChange={(e) => setSectionSpacing(Number(e.target.value))}
                      disabled={sectionSpacingAuto}
                      className="flex-1 h-2 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed accent-primary-600"
                    />
                    <span className="text-sm text-gray-600 w-10">{sectionSpacingAuto ? '—' : sectionSpacing.toFixed(1)}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">Auto uses the style default; adjust slider for custom section gaps.</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="section-line-breaks"
                  checked={sectionLineBreaks}
                  onChange={(e) => setSectionLineBreaks(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="section-line-breaks" className="text-sm text-gray-700">
                  Horizontal divider lines between sections
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="btn-outline btn-md"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary btn-md"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingStyleId ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
            {/* Right: live preview */}
            <div className="lg:pl-4 border-t lg:border-t-0 lg:border-l border-gray-100 pt-6 lg:pt-0">
              <StylePreview
                headerColor={headerColor}
                sectionColor={sectionColor}
                bodyTextColor={bodyTextColor}
                accentColor={accentColor}
                fontFamily={fontFamily}
                baseStyle={baseStyle}
                lineSpacing={lineSpacingAuto ? 1 : lineSpacing}
                sectionSpacing={sectionSpacingAuto ? 1 : sectionSpacing}
                sectionLineBreaks={sectionLineBreaks}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResumeStyleModal;
